// SalesCalculator.tsx
import { useState, useEffect, useMemo, useRef } from "react";
import { Card, CardContent, CardHeader } from "../components/ui/Card";
import { Input } from "../components/ui/Input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/Checkbox";
import { ScrollArea } from "../components/ui/scroll-area.tsx";
import { Separator } from "../components/ui/separator.tsx";
import { 
  Calculator, 
  TrendingUp, 
  Users, 
  MapPin, 
  DollarSign, 
  Percent, 
  X,
  ChevronDown,
  ChevronUp,
  Briefcase,
  Target,
  BarChart3,
  Zap,
  Filter,
  // ShoppingBag,
  PieChart,
  Home,
  Car,
  GraduationCap,
  Baby,
  User,
  UsersRound,
  Building2,
  Plane,
  Briefcase as WorkIcon,
  Stethoscope,
  School,
  // Heart,
  Truck
} from "lucide-react";
import { useServiceCategories } from "../hooks/usePartnerServices";
import { getPublicPlans } from "../apis/partnerForm";

// Types
interface Plan {
  id: string;
  name: string;
  displayName: string;
  features?: {
    hexCount: number;
    coverageAreaKm2?: number;
  };
}

interface PopulationDataItem {
  label: string;
  value: number;
  key: string;
  category: string;
  icon: any;
}

interface ServiceCategory {
  category: string;
  subcategories: string[];
  categoryType?: string;
}

// Population data per 1 sq km (900 population base) with icons
const POPULATION_DATA: PopulationDataItem[] = [
  { label: "Total Population", value: 900, key: "totalPopulation", category: "total", icon: Users },
  { label: "Gents / Men", value: 432, key: "men", category: "gender", icon: User },
  { label: "Ladies / Women", value: 468, key: "women", category: "gender", icon: UsersRound },
  { label: "Kids (0-5)", value: 54, key: "kids0_5", category: "age", icon: Baby },
  { label: "Kids (Class 1-4)", value: 72, key: "kids1_4", category: "education", icon: GraduationCap },
  { label: "Students Class 5-7", value: 63, key: "students5_7", category: "education", icon: GraduationCap },
  { label: "Students Class 8-10", value: 63, key: "students8_10", category: "education", icon: GraduationCap },
  { label: "10th Students", value: 14, key: "students10", category: "education", icon: GraduationCap },
  { label: "Plus 1 Plus 2 students", value: 18, key: "students11_12", category: "education", icon: GraduationCap },
  { label: "Fresh +2 Passouts Yearly", value: 9, key: "freshPassouts", category: "education", icon: GraduationCap },
  { label: "Degree Students", value: 27, key: "degreeStudents", category: "education", icon: GraduationCap },
  { label: "Fresh Graduates Yearly", value: 9, key: "freshGraduates", category: "education", icon: GraduationCap },
  { label: "Degree Holders", value: 162, key: "degreeHolders", category: "education", icon: GraduationCap },
  { label: "Recent Graduates (Within 3 Years)", value: 40, key: "recentGraduates", category: "education", icon: GraduationCap },
  { label: "Unemployed Graduates", value: 45, key: "unemployedGraduates", category: "employment", icon: WorkIcon },
  { label: "Youth (18-35)", value: 216, key: "youth", category: "age", icon: User },
  { label: "Middle Age", value: 306, key: "middleAge", category: "age", icon: User },
  { label: "Senior Citizens", value: 162, key: "seniorCitizens", category: "age", icon: User },
  { label: "CBSE Students", value: 22, key: "cbseStudents", category: "education", icon: School },
  { label: "ICSE Students", value: 3, key: "icseStudents", category: "education", icon: School },
  { label: "Government School Students", value: 72, key: "govtSchoolStudents", category: "education", icon: School },
  { label: "Tuition Going Students", value: 72, key: "tuitionStudents", category: "education", icon: GraduationCap },
  { label: "Nurses", value: 15, key: "nurses", category: "profession", icon: Stethoscope },
  { label: "Nursing Students", value: 6, key: "nursingStudents", category: "education", icon: GraduationCap },
  { label: "Nurses Planning Abroad", value: 4, key: "nursesAbroad", category: "profession", icon: Plane },
  { label: "Abroad Aspirants Total", value: 18, key: "abroadAspirants", category: "aspiration", icon: Plane },
  { label: "Job Seekers", value: 72, key: "jobSeekers", category: "employment", icon: WorkIcon },
  { label: "Migration Looking Families", value: 27, key: "migrationFamilies", category: "family", icon: Home },
  { label: "Houses / Families", value: 225, key: "houses", category: "family", icon: Home },
  { label: "Nuclear Families", value: 155, key: "nuclearFamilies", category: "family", icon: Home },
  { label: "Apartments / Flats", value: 35, key: "apartments", category: "housing", icon: Building2 },
  { label: "New Houses Constructed Yearly", value: 5, key: "newHouses", category: "housing", icon: Home },
  { label: "Home Renovations Yearly", value: 18, key: "homeRenovations", category: "housing", icon: Home },
  { label: "2 Wheelers", value: 160, key: "twoWheelers", category: "vehicles", icon: Car },
  { label: "Cars / 4 Wheelers", value: 50, key: "fourWheelers", category: "vehicles", icon: Car },
  { label: "Commercial Vehicles", value: 8, key: "commercialVehicles", category: "vehicles", icon: Truck },
];

type GroupedPopulationData = {
  [key: string]: PopulationDataItem[];
};

const categoryConfig: Record<string, { label: string; icon: any }> = {
  total: { label: "Overall Population", icon: PieChart },
  gender: { label: "Gender Distribution", icon: UsersRound },
  age: { label: "Age Groups", icon: User },
  education: { label: "Education & Students", icon: GraduationCap },
  employment: { label: "Employment", icon: Briefcase },
  profession: { label: "Profession", icon: Stethoscope },
  aspiration: { label: "Aspirations", icon: Plane },
  family: { label: "Family & Housing", icon: Home },
  housing: { label: "Housing Details", icon: Building2 },
  vehicles: { label: "Vehicles", icon: Car },
};

const StatCard = ({ icon: Icon, label, value, subtext, color, trend }: any) => (
  <div className={`rounded-xl border bg-gradient-to-br ${color} p-4 shadow-sm hover:shadow-md transition-all`}>
    <div className="flex items-start justify-between">
      <div className="flex-1">
        <p className="text-xs font-medium text-muted-foreground">{label}</p>
        <p className="text-2xl md:text-3xl font-bold mt-1">{value.toLocaleString()}</p>
        {subtext && <p className="text-xs text-muted-foreground mt-1">{subtext}</p>}
        {trend && (
          <div className="flex items-center gap-1 mt-2">
            <TrendingUp className="h-3 w-3 text-green-600" />
            <span className="text-xs text-green-600">{trend}</span>
          </div>
        )}
      </div>
      <div className="rounded-full bg-white/20 p-2 shrink-0 ml-3">
        <Icon className="h-4 w-4 md:h-5 md:w-5" />
      </div>
    </div>
  </div>
);

const SectionHeader = ({ title, description, icon: Icon }: any) => (
  <div className="flex items-start gap-3 mb-4">
    <div className="rounded-lg bg-primary/10 p-2 shrink-0">
      <Icon className="h-4 w-4 md:h-5 md:w-5 text-primary" />
    </div>
    <div className="flex-1">
      <h3 className="font-semibold text-foreground text-base md:text-lg">{title}</h3>
      {description && <p className="text-xs md:text-sm text-muted-foreground">{description}</p>}
    </div>
  </div>
);

export default function SalesCalculator() {
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [selectedSubcategories, setSelectedSubcategories] = useState<string[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [selectedPopulationCategories, setSelectedPopulationCategories] = useState<string[]>([]);
  const [offerPercentage, setOfferPercentage] = useState<number | string>("");
  const [avgBilling, setAvgBilling] = useState<number | string>("");
  const [plans, setPlans] = useState<Plan[]>([]);
  const [isLoadingPlans, setIsLoadingPlans] = useState<boolean>(true);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(["total", "gender", "age"]));
  const [catSearch, setCatSearch] = useState<string>("");
  const [debouncedCatSearch, setDebouncedCatSearch] = useState<string>("");
  const [isCatDropdownOpen, setIsCatDropdownOpen] = useState<boolean>(false);
  const isSelectingRef = useRef<boolean>(false);

  useEffect(() => {
    setCatSearch(selectedCategory);
  }, [selectedCategory]);

  useEffect(() => {
    if (catSearch === "") {
      setDebouncedCatSearch("");
      return;
    }
    const handler = setTimeout(() => {
      setDebouncedCatSearch(catSearch);
    }, 300);
    return () => clearTimeout(handler);
  }, [catSearch]);

  const { data: serviceCategories = [], isLoading: categoriesLoading } = useServiceCategories(debouncedCatSearch);

  const filteredCategories = serviceCategories;

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setIsLoadingPlans(true);
        const response = await getPublicPlans();
        setPlans(response);
      } catch (error) {
        console.error("Failed to fetch plans:", error);
      } finally {
        setIsLoadingPlans(false);
      }
    };
    fetchPlans();
  }, []);

  const currentCategory = useMemo(() => {
    return serviceCategories.find((cat: ServiceCategory) => cat.category === selectedCategory);
  }, [serviceCategories, selectedCategory]);

  const subcategoryOptions = useMemo(() => {
    return currentCategory?.subcategories || [];
  }, [currentCategory]);

  const toggleSubcategory = (subcategory: string) => {
    setSelectedSubcategories(prev =>
      prev.includes(subcategory) ? prev.filter(s => s !== subcategory) : [...prev, subcategory]
    );
  };

  const togglePopulationCategory = (category: PopulationDataItem) => {
    setSelectedPopulationCategories(prev =>
      prev.includes(category.key) ? prev.filter(c => c !== category.key) : [...prev, category.key]
    );
  };

  const toggleSection = (section: string) => {
    const newSet = new Set(expandedSections);
    if (newSet.has(section)) {
      newSet.delete(section);
    } else {
      newSet.add(section);
    }
    setExpandedSections(newSet);
  };

  const totalAreaKm2 = useMemo(() => {
    if (!selectedPlan) return 0;
    const hexCount = selectedPlan.features?.hexCount || 0;
    return hexCount * 5.16;
  }, [selectedPlan]);

  const totalTargetedCustomers = useMemo(() => {
    const selectedData = POPULATION_DATA.filter(p => selectedPopulationCategories.includes(p.key));
    const sum = selectedData.reduce((acc, curr) => acc + curr.value, 0);
    return Math.round(sum * totalAreaKm2);
  }, [selectedPopulationCategories, totalAreaKm2]);

  const totalMarketSize = totalTargetedCustomers;
  
  const totalSalesPossibility = useMemo(() => {
    if (totalMarketSize === 0) return 0;
    const numericPercentage = parseFloat(String(offerPercentage)) || 0;
    const customers = Math.floor(totalMarketSize * (numericPercentage / 100));
    const numericAvgBilling = parseFloat(String(avgBilling)) || 0;
    return customers * numericAvgBilling;
  }, [totalMarketSize, offerPercentage, avgBilling]);

  const groupedPopulationData = useMemo(() => {
    const groups: GroupedPopulationData = {
      total: [], gender: [], age: [], education: [], employment: [],
      profession: [], aspiration: [], family: [], housing: [], vehicles: []
    };
    POPULATION_DATA.forEach(item => {
      if (groups[item.category]) groups[item.category].push(item);
    });
    return groups;
  }, []);

  const selectedCount = selectedPopulationCategories.length;

  if (isLoadingPlans) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4 px-4">
        <div className="relative">
          <div className="animate-spin rounded-full h-10 w-10 md:h-12 md:w-12 border-b-2 border-primary"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <Calculator className="h-4 w-4 md:h-5 md:w-5 text-primary opacity-50" />
          </div>
        </div>
        <p className="text-sm text-muted-foreground">Loading calculator...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100/50 p-3 md:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto space-y-4 md:space-y-6">
        {/* Header - Mobile Optimized */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="rounded-xl bg-primary/10 p-2.5 shrink-0">
              <Calculator className="h-5 w-5 md:h-6 md:w-6 text-primary" />
            </div>
            <div>
              <h1 className="text-xl md:text-2xl lg:text-3xl font-bold tracking-tight">Sales Calculator</h1>
              <p className="text-xs md:text-sm text-muted-foreground mt-0.5">
                Estimate market size & sales potential
              </p>
            </div>
          </div>
          {selectedPlan && (
            <div className="flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1.5 md:px-4 md:py-2 self-start sm:self-auto">
              <Zap className="h-3 w-3 md:h-4 md:w-4 text-primary" />
              <span className="text-xs md:text-sm font-medium">{selectedPlan.displayName}</span>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
          {/* Left Panel - Configuration */}
          <div className="lg:col-span-1 space-y-4">
            {/* Business Details Card */}
            <Card className="border-0 shadow-md">
              <CardHeader className="pb-2 md:pb-3">
                <SectionHeader title="Business Details" description="Configure your business" icon={Briefcase} />
              </CardHeader>
              <CardContent className="space-y-4 md:space-y-5">
                <div className="space-y-2 relative">
                  <Label className="text-xs md:text-sm font-medium">Business Category</Label>
                  <div className="relative">
                    <input
                      type="text"
                      className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 pr-8"
                      placeholder="Search or select category..."
                      value={catSearch}
                      onFocus={() => {
                        setIsCatDropdownOpen(true);
                        setCatSearch("");
                      }}
                      onChange={(e) => {
                        setCatSearch(e.target.value);
                        setIsCatDropdownOpen(true);
                      }}
                      onBlur={() => {
                        setTimeout(() => {
                          if (!isSelectingRef.current) {
                            setIsCatDropdownOpen(false);
                            setCatSearch(selectedCategory);
                          }
                          isSelectingRef.current = false;
                        }, 200);
                      }}
                    />
                    <div className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none">
                      <ChevronDown className="h-4 w-4" />
                    </div>
                  </div>
                  
                  {isCatDropdownOpen && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-input rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {categoriesLoading ? (
                        <div className="px-3 py-2 text-xs text-slate-500 flex items-center gap-2">
                          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-primary"></div>
                          Searching categories...
                        </div>
                      ) : filteredCategories.length === 0 ? (
                        <div className="px-3 py-2 text-xs text-slate-500">No categories found</div>
                      ) : (
                        filteredCategories.map((cat: ServiceCategory) => (
                          <button
                            key={cat.category}
                            type="button"
                            className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-100 transition-colors ${
                              selectedCategory === cat.category ? "bg-slate-100 font-medium text-primary" : "text-slate-700"
                            }`}
                            onMouseDown={() => {
                              isSelectingRef.current = true;
                            }}
                            onClick={() => {
                              setSelectedCategory(cat.category);
                              setCatSearch(cat.category);
                              setIsCatDropdownOpen(false);
                            }}
                          >
                            {cat.category}
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>

                {selectedCategory && (
                  <div className="space-y-2">
                    <Label className="text-xs md:text-sm font-medium">Service Subcategories</Label>
                    <div className="rounded-lg border border-input bg-background p-3 max-h-40 overflow-y-auto">
                      {subcategoryOptions.length === 0 ? (
                        <p className="text-xs text-muted-foreground text-center py-4">No subcategories available</p>
                      ) : (
                        <div className="space-y-2">
                          {subcategoryOptions.map((sub: string) => (
                            <div key={sub} className="flex items-center space-x-2">
                              <Checkbox
                                id={`sub-${sub}`}
                                checked={selectedSubcategories.includes(sub)}
                                onChange={() => toggleSubcategory(sub)}
                              />
                              <Label htmlFor={`sub-${sub}`} className="text-xs md:text-sm cursor-pointer flex-1">
                                {sub}
                              </Label>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    {selectedSubcategories.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {selectedSubcategories.map((sub: string) => (
                          <span key={sub} className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                            {sub.length > 20 ? `${sub.substring(0, 20)}...` : sub}
                            <X className="h-3 w-3 cursor-pointer" onClick={() => toggleSubcategory(sub)} />
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <div className="space-y-2">
                  <Label className="text-xs md:text-sm font-medium">Select Plan</Label>
                  <select
                    className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                    value={selectedPlan?.id || ""}
                    onChange={(e) => {
                      const plan = plans.find(p => p.id === e.target.value);
                      setSelectedPlan(plan || null);
                    }}
                  >
                    <option value="">Select plan...</option>
                    {plans.map((plan: Plan) => (
                      <option key={plan.id} value={plan.id}>
                        {plan.displayName} - {plan.features?.hexCount || 0} hex
                      </option>
                    ))}
                  </select>
                </div>
              </CardContent>
            </Card>

            {/* Coverage Summary Card */}
            {selectedPlan && (
              <Card className="border-0 shadow-md bg-gradient-to-br from-primary/5 to-primary/10">
                <CardContent className="pt-4 md:pt-6">
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-xs md:text-sm text-muted-foreground">Total Area Covered</span>
                      <span className="text-lg md:text-xl font-bold text-primary">{totalAreaKm2.toFixed(2)} km²</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs md:text-sm text-muted-foreground">Hexagons</span>
                      <span className="font-semibold text-sm md:text-base">{selectedPlan.features?.hexCount || 0}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs md:text-sm text-muted-foreground">Plan Type</span>
                      <span className="font-semibold text-sm md:text-base capitalize">{selectedPlan.displayName}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right Panel - Population & Calculations */}
          <div className="lg:col-span-2 space-y-4">
            {/* Target Audience Selection */}
            <Card className="border-0 shadow-md">
              <CardHeader className="pb-2 md:pb-3">
                <SectionHeader title="Target Audience" description="Select population segments" icon={Target} />
                <div className="flex justify-between items-center mt-2">
                  <p className="text-xs text-muted-foreground">Based on 1 km² = 900 people</p>
                  {selectedCount > 0 && (
                    <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary flex items-center gap-1">
                      <Filter className="h-3 w-3" />
                      {selectedCount} selected
                    </span>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px] md:h-[450px] pr-2 md:pr-4">
                  {Object.entries(groupedPopulationData).map(([key, items]) => {
                    if (items.length === 0) return null;
                    const isExpanded = expandedSections.has(key);
                    const selectedInSection = items.filter(i => selectedPopulationCategories.includes(i.key)).length;
                    const Icon = categoryConfig[key]?.icon || PieChart;
                    
                    return (
                      <div key={key} className="mb-2 md:mb-3 border rounded-lg overflow-hidden">
                        <button
                          onClick={() => toggleSection(key)}
                          className="w-full flex items-center justify-between p-3 hover:bg-muted/50 transition-colors text-left"
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
                            <span className="font-medium text-sm md:text-base truncate">
                              {categoryConfig[key]?.label || key}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            {selectedInSection > 0 && (
                              <span className="text-xs text-primary">{selectedInSection}</span>
                            )}
                            {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                          </div>
                        </button>
                        {isExpanded && (
                          <div className="p-3 pt-0 border-t">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-3">
                              {items.map((item: PopulationDataItem) => {
                                const ItemIcon = item.icon;
                                return (
                                  <div key={item.key} className="flex items-center space-x-2 min-w-0">
                                    <Checkbox
                                      id={`pop-${item.key}`}
                                      checked={selectedPopulationCategories.includes(item.key)}
                                      onChange={() => togglePopulationCategory(item)}
                                    />
                                    <Label htmlFor={`pop-${item.key}`} className="text-xs md:text-sm cursor-pointer flex justify-between w-full gap-2">
                                      <span className="truncate flex items-center gap-1">
                                        <ItemIcon className="h-3 w-3 text-muted-foreground shrink-0" />
                                        {item.label}
                                      </span>
                                      <span className="text-muted-foreground text-xs shrink-0">{item.value}</span>
                                    </Label>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </ScrollArea>
              </CardContent>
            </Card>

            {/* Market Size Stats - Mobile Responsive */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
              <StatCard
                icon={Users}
                label="Total Targeted Customers"
                value={totalTargetedCustomers}
                subtext={`× ${totalAreaKm2.toFixed(2)} km²`}
                color="from-blue-50 to-indigo-50 border-blue-100"
              />
              <StatCard
                icon={MapPin}
                label="Total Market Size"
                value={totalMarketSize}
                subtext="Potential customers"
                color="from-purple-50 to-pink-50 border-purple-100"
              />
            </div>

            {/* Sales Projection */}
            <Card className="border-0 shadow-md">
              <CardHeader className="pb-2 md:pb-3">
                <SectionHeader title="Sales Projection" description="Estimate revenue" icon={BarChart3} />
              </CardHeader>
              <CardContent className="space-y-4 md:space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2 text-xs md:text-sm font-medium">
                      <Percent className="h-3 w-3 md:h-4 md:w-4 text-primary" />
                      Offer Adoption Rate
                    </Label>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Input
                        type="number"
                        value={offerPercentage}
                        onChange={(e) => setOfferPercentage(e.target.value)}
                        className="w-24 md:w-28"
                      />
                      <span className="text-xs md:text-sm text-muted-foreground">%</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      ≈ {Math.floor(totalMarketSize * ((parseFloat(String(offerPercentage)) || 0) / 100)).toLocaleString()} customers
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2 text-xs md:text-sm font-medium">
                      <DollarSign className="h-3 w-3 md:h-4 md:w-4 text-primary" />
                      Avg Transaction Value
                    </Label>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">₹</span>
                      <Input
                        type="number"
                        value={avgBilling}
                        onChange={(e) => setAvgBilling(e.target.value)}
                        className="w-28 md:w-32"
                      />
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Total Sales Card - Mobile Optimized */}
                <div className="rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 p-4 md:p-5 text-white shadow-lg">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                    <div className="flex-1">
                      <p className="text-xs md:text-sm font-medium text-white/80">Projected Annual Revenue</p>
                      <p className="text-2xl md:text-3xl lg:text-4xl font-bold mt-1 break-words">
                        ₹{totalSalesPossibility.toLocaleString()}
                      </p>
                      <div className="grid grid-cols-3 gap-2 mt-3 text-xs md:text-sm">
                        <div>
                          <p className="text-white/70">Customers</p>
                          <p className="font-semibold">{Math.floor(totalMarketSize * ((parseFloat(String(offerPercentage)) || 0) / 100)).toLocaleString()}</p>
                        </div>
                        <div>
                          <p className="text-white/70">Avg Bill</p>
                          <p className="font-semibold">₹{(parseFloat(String(avgBilling)) || 0).toLocaleString()}</p>
                        </div>
                        <div>
                          <p className="text-white/70">Market</p>
                          <p className="font-semibold">{totalMarketSize.toLocaleString()}</p>
                        </div>
                      </div>
                    </div>
                    <div className="rounded-full bg-white/20 p-2.5 shrink-0 self-start">
                      <TrendingUp className="h-5 w-5 md:h-6 md:w-6" />
                    </div>
                  </div>
                </div>

                <p className="text-xs text-muted-foreground text-center pt-2">
                  * Estimates based on selected parameters. Actual results may vary.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}